import * as uiAnalyticsConsumer from '@servicenow/ui-analytics-consumer';
import {mark, types as metrics, getInteractionId} from '@servicenow/ui-metrics';
import {createHttpErrorAction} from '@servicenow/ui-effect-helpers';
import sendRequest from './sendRequest';
import replacePathParameters from './replacePathParams';
import {
	get,
	pick,
	isEqual,
	addPendingNetworkActionForHost,
	removePendingNetworkActionForHost,
	defer,
	getSpanOptions
} from '@servicenow/ui-internal';

import {configConstants, getConfigItem} from '@servicenow/ui-config';

import log from './log';
import {
	SPAN_ATTR_HTTP_BATCH,
	SPAN_ATTR_HTTP_CACHEABLE,
	SPAN_ATTR_HTTP_METHOD,
	SPAN_ATTR_HTTP_URL,
	SPAN_FOR_HTTP,
	SPAN_KIND_CLIENT,
	SPAN_TRACE_PARENT
} from './constants';

export const HTTP_REQUEST_STARTED = 'HTTP_REQUEST_STARTED';
export const HTTP_REQUEST_PROGRESS_UPDATED = 'HTTP_REQUEST_PROGRESS_UPDATED';
export const HTTP_REQUEST_SUCCEEDED = 'HTTP_REQUEST_SUCCEEDED';
export const HTTP_REQUEST_FAILED = 'HTTP_REQUEST_FAILED';
export const HTTP_CACHE_UPDATED = 'HTTP_CACHE_UPDATED';
export const HTTP_EFFECT_SESSION_NOTIFICATIONS_RECEIVED =
	'HTTP_EFFECT_SESSION_NOTIFICATIONS_RECEIVED';

export const HEADER_X_WANT_AUTH_SESSION_NOTIFICATION_MESSAGES = 'X-WantAuthSessionNotifications';

const {HTTP_CACHING_REFRESH_DELAY} = configConstants;
const {trace, createTraceParentHeader, isValidTraceId} = uiAnalyticsConsumer;

const cacheDelay = getConfigItem(HTTP_CACHING_REFRESH_DELAY) || 10000;

export const createMultipartConfig = (
	{partReceivedActionType, parsePartsAsJson, emitCompletedResponse},
	dispatch,
	metaData
) => {
	let multipartConfig;
	if (partReceivedActionType) {
		multipartConfig = {
			multipartConsumer: partRecvd =>
				dispatch(partReceivedActionType, partRecvd?.body, {
					...metaData,
					responseHeaders: partRecvd?.headers
				})
		};

		multipartConfig.parsePartsAsJson =
			// eslint-disable-next-line eqeqeq
			parsePartsAsJson != undefined ? Boolean(parsePartsAsJson) : true;
		multipartConfig.emitCompletedResponse =
			// eslint-disable-next-line eqeqeq
			emitCompletedResponse != undefined ? Boolean(emitCompletedResponse) : true;
	}

	return multipartConfig;
};

export function* httpEffect(url, options = {}, coeffects) {
	const {
		method = 'GET',
		headers = {},
		headerList = [],
		pathParams = [],
		queryParams = [],
		dataParam = 'data',
		batch = true,
		cacheable = false,
		encodeURIComponent = true,
		cancelPromiseSymbol,
		startActionType = HTTP_REQUEST_STARTED,
		progressActionType = HTTP_REQUEST_PROGRESS_UPDATED,
		successActionType = HTTP_REQUEST_SUCCEEDED,
		errorActionType = HTTP_REQUEST_FAILED,
		cacheUpdateActionType = HTTP_CACHE_UPDATED,
		streaming = {}
	} = options;

	const {action, dispatch, host} = coeffects;
	const {payload} = action;
	const updatedUrl = replacePathParameters(url, pick(payload, pathParams), {
		encodeURIComponent
	});
	const params = pick(payload, queryParams);
	const data = get(payload, dataParam, undefined);
	const parentSpan = action.meta.span;
	const httpSpan =
		parentSpan &&
		trace(SPAN_FOR_HTTP, {
			...getSpanOptions(host),
			kind: SPAN_KIND_CLIENT,
			traceId: parentSpan.traceId,
			...(isValidTraceId(parentSpan.traceId) && {parentSpan})
		});
	const metaData = {
		...action.meta,
		...(httpSpan && isValidTraceId(httpSpan.traceId) && {span: httpSpan}),
		request: {
			url,
			updatedUrl,
			params,
			data,
			...options
		}
	};
	const multipartConfig = createMultipartConfig(streaming, dispatch, metaData);

	function onUploadProgress(progressEvent) {
		dispatch(progressActionType, {progressEvent}, metaData);
	}

	addPendingNetworkActionForHost(action, host);
	try {
		dispatch(startActionType, {}, metaData);
		const interactionId = getInteractionId(action.meta);

		if (updatedUrl.indexOf('/null') !== -1 || updatedUrl.indexOf('/undefined') !== -1) {
			const {tagName, nowId} = host;
			log(
				`An HTTP request with the url of ${updatedUrl} was made. Please verify that ${updatedUrl} is a valid URL.`,
				{
					tagName,
					componentId: nowId,
					host,
					origin: 'httpEffect'
				}
			);
		}

		/* Required by WebAPI team to send `session.notifications` as part of the responses */
		if (headers[HEADER_X_WANT_AUTH_SESSION_NOTIFICATION_MESSAGES] == null) {
			headers[HEADER_X_WANT_AUTH_SESSION_NOTIFICATION_MESSAGES] = true;
		}
		headers['NOW-UI-INTERACTION'] = interactionId;
		if (window.nowUiFramework && window.nowUiFramework.uxrInteractionId) {
			headers['NOW-UX-INTERACTION'] =
				window.nowUiFramework.getUxrInteractionIdByHost?.(host) ||
				window.nowUiFramework.uxrInteractionId;
		}
		if (httpSpan && isValidTraceId(httpSpan.traceId)) {
			httpSpan.setAttribute(SPAN_ATTR_HTTP_URL, updatedUrl);
			httpSpan.setAttribute(SPAN_ATTR_HTTP_METHOD, method);
			httpSpan.setAttribute(SPAN_ATTR_HTTP_BATCH, batch);
			httpSpan.setAttribute(SPAN_ATTR_HTTP_CACHEABLE, cacheable);
		}

		mark(host, interactionId, metrics.HTTP, {
			path: updatedUrl,
			type: action.type,
			batch
		});

		const traceparent = httpSpan && createTraceParentHeader(httpSpan.id, httpSpan.traceId);
		if (httpSpan && isValidTraceId(httpSpan.traceId))
			httpSpan.setAttribute(SPAN_TRACE_PARENT, traceparent);
		const request = sendRequest(updatedUrl, method, {
			headers: {
				...pick(payload, headerList),
				...headers
			},
			params,
			...(data && {data}),
			onUploadProgress,
			batch,
			cacheable,
			cancelPromiseSymbol,
			multipartConfig,
			span: httpSpan && isValidTraceId(httpSpan.traceId) ? httpSpan : null
		});

		let result = yield request;
		if (httpSpan && isValidTraceId(httpSpan.traceId)) metaData.span = httpSpan;
		const {isCachedResult = false, requestPromise = async () => {}} = result;
		if (!isCachedResult) {
			result = yield requestPromise();
		}

		const {
			headers: responseHeaders,
			data: successData,
			refetchPromise = null,
			status,
			statusText
		} = result;

		if (isCachedResult && refetchPromise) {
			dispatch(successActionType, successData, {...metaData, responseHeaders, status, statusText});
			defer(() => {
				refetchPromise().then(refetchResult => {
					// compare cachedResult and refetchResult
					if (!isEqual(successData, refetchResult.data)) {
						dispatch(cacheUpdateActionType, refetchResult.data, {
							...metaData,
							responseHeaders: refetchResult.headers,
							status: refetchResult.status,
							statusText: refetchResult.statusText
						});
					}
				});
			}, cacheDelay);
		} else {
			dispatch(successActionType, successData, {...metaData, responseHeaders, status, statusText});
			successData?.session &&
				dispatch(HTTP_EFFECT_SESSION_NOTIFICATIONS_RECEIVED, successData.session, {
					...metaData,
					responseHeaders,
					status,
					statusText
				});
		}
	} catch (error) {
		metaData.responseHeaders = get(error, 'options.responseHeaders', null);
		metaData.status = error.status;
		metaData.statusText = error.statusText;
		dispatch(errorActionType, error, metaData, true);

		// Standard action dispatched on HTTP errors from the server
		const {status, statusText, message, data} = error;
		const {
			type,
			payload,
			meta,
			error: httpError
		} = createHttpErrorAction(status, statusText, message, data, metaData);
		dispatch(
			type,
			payload,
			httpSpan && isValidTraceId(httpSpan.traceId) ? {...meta, span: httpSpan} : meta,
			httpError
		);
	} finally {
		httpSpan && httpSpan.end();
		removePendingNetworkActionForHost(action, host);
	}
}

/**
 * Effect that makes an HTTP request and dispatches actions at relevant parts of the life cycle:
 *  - Before the request is sent
 *  - After the request has returned (success or error)
 *  - Upload of progress updates
 *
 * The Success and Action Types that are returned also contain a `meta` property with helpful information
 * useful for debugging purposes including the response headers.
 *
 * @function createHttpEffect
 * @example
 * import {createHttpEffect} from '@servicenow/ui-effect-http';
 *
 * const USER_FETCH_SUCCESS = 'USER_FETCH_SUCCESS'
 *
 * const fetchUserEffect = createHttpEffect('api/users/:id', {
 *      method: 'POST'
 *      headers: {},
 *      pathParams: ['id']
 *      dataParam: 'data',
 *      successActionType: USER_FETCH_SUCCESS
 * });
 *
 * // Register with component action handlers:
 * {
 *     actionHandlers: {
 *         'USER_FETCHED': fetchUserEffect,
 *         [USER_FETCH_SUCCESS]: ({action, updateState}) => {...}
 *     }
 * }
 *
 * @param {String} url - URL to endpoint
 * @param {Object} options
 * @param {String} options.method - Request method: GET, POST, PUT, PATCH, DELETE (default: GET)
 * @param {Object} options.headers - Request headers
 * @param {Object<String>} options.headerList - dynamic HTTP request headers
 * @param {Boolean} options.batch - Enable or disable batching for requests made by this effect (default: true)
 * @param {Boolean} options.encodeURIComponent - Encode URL (default: true)
 * @param {String} options.dataParam - Property in dispatched action to map to the data property of the HTTP request
 * @param {Array<String>} options.pathParams - Array of properties in dispatched action to map values to dynamic path variables ("api/users/:id")
 * @param {Array<String>} options.queryParams - Array of properties in dispatched action to map values to URL query string variables ("api/users?id=abc123)
 * @param {String} options.startActionType - Action to be dispatched when the HTTP request has been issued
 * @param {Object} options.streaming -  JSON object to describe how to handle data when a streaming (multipart/mixed) response is expected
 * @param options.&lt;streaming&gt;.partReceivedActionType {String} - The action to be dispatched when a streaming data part has been received
 * @param options.&lt;streaming&gt;.parsePartsAsJson {Boolean} - Parse each received data part as JSON (default: true)
 * @param options.&lt;streaming&gt;.emitCompletedResponse {Boolean} - Also emit the full response payload via the successActionType (default: true)
 * @param {String} options.progressActionType - Action to be dispatched when onprogress is triggered during the HTTP request
 * @param {String} options.successActionType - Action to be dispatched when the HTTP request has successfully completed. The response data will be available as payload which will be of type object for application/json content-type and String for others
 * @param {String} options.errorActionType - Action to be dispatched when the HTTP request has failed
 *
 * @return {Object} Http Effect Descriptor
 */
export default function createHttpEffect(url, options) {
	return {
		effect: httpEffect,
		args: [url, options]
	};
}
